import { stripHexPrefix } from '@ethereumjs/util';
import type { KeyPairSigner } from '@solana/kit';
import {
  Endian,
  address,
  createKeyPairFromPrivateKeyBytes,
  createSignableMessage,
  createSignerFromKeyPair,
  createSolanaRpc,
  getBase58Codec,
  getU64Codec,
} from '@solana/kit';
import { HDKey } from 'micro-key-producer/slip10';
import { hexToBytes, keccak256 } from 'viem';

import { SOLANA_RPC_ENDPOINT } from 'src/config';

import { computeBridgeFact as computeEthereumBridgeFact } from '../ethereum/contracts';

const PATH = "m/44'/501'/0'/0'"; // Standard Solana path

interface TransferParams {
  bankProgramAddress: string;
  authority: string;
  senderAddress: string;
  receiverAddress: string;
  amount: bigint;
  feeAmount: bigint;
  nonce: string;
  expirationTimestamp: number;
  originator: string;
  data?: string;
}

interface ConditionalTransferParams extends TransferParams {
  conditionalAmount: bigint;
  conditionalCurrency: string;
  conditionalNetwork: string;
  conditionalReceiverAddress: string;
  conditionalSalt: string;
}

export class SolanaWallet {
  public static async load(ethereumPrivateKey: string) {
    const keypair = await this.deriveSolanaKeypair(ethereumPrivateKey, {
      extractable: true,
    });
    const signer = await createSignerFromKeyPair(keypair);

    // In order to extract privateKeyBytes, we need to set key derivation to extractable
    const privateKeyBytes = new Uint8Array(
      await crypto.subtle.exportKey('pkcs8', keypair.privateKey)
    ).slice(16);
    const publicKeyBytes = new Uint8Array(
      await crypto.subtle.exportKey('raw', keypair.publicKey)
    );

    return new SolanaWallet(
      new Uint8Array([...privateKeyBytes, ...publicKeyBytes]),
      signer
    );
  }

  private privKey: string;

  private privKeyBytes: Uint8Array;

  private signer: KeyPairSigner;

  private codec: ReturnType<typeof getBase58Codec>;

  public constructor(privateKey: Uint8Array, signer: KeyPairSigner) {
    this.codec = getBase58Codec();
    this.privKeyBytes = privateKey;
    this.privKey = this.codec.decode(privateKey);
    this.signer = signer;
  }

  public get address() {
    return this.signer.address;
  }

  public get privateKey() {
    return this.privKey;
  }

  public get privateKeyBytes() {
    return this.privKeyBytes;
  }

  private loadedRpcClient?: ReturnType<typeof createSolanaRpc>;

  private get walletClient() {
    if (!this.loadedRpcClient) {
      this.loadedRpcClient = createSolanaRpc(SOLANA_RPC_ENDPOINT);
    }

    return this.loadedRpcClient;
  }

  public async getBalance() {
    return await this.walletClient.getBalance(address(this.address)).send();
  }

  public async sign(messageBytes: Uint8Array) {
    const mess = createSignableMessage(messageBytes);
    const [ret] = await this.signer.signMessages([mess]);
    return ret[this.address];
  }

  private static async deriveSolanaKeypair(
    ethereumPrivateKey: string,
    { extractable }: { extractable?: boolean }
  ) {
    const key = HDKey.fromMasterSeed(
      hexToBytes(`0x${stripHexPrefix(ethereumPrivateKey)}`)
    ).derive(PATH);
    const { privateKey, publicKey } = await createKeyPairFromPrivateKeyBytes(
      key.privateKey,
      extractable
    );
    return { privateKey, publicKey };
  }

  /** @returns base58 encoded signature */
  async signTokenTransferRequest({
    transferProxyProgramAddress,
    merkleTreeAddress,
    leafIndex,
    nonce,
    expirationTimestamp,
    receiverAddress,
    originator,
  }: {
    transferProxyProgramAddress: string;
    merkleTreeAddress: string;
    leafIndex: number;
    nonce: string;
    expirationTimestamp: number;
    receiverAddress: string;
    originator: string;
  }): Promise<string> {
    const payload = [
      'TRANSFER',
      transferProxyProgramAddress,
      merkleTreeAddress,
      leafIndex.toString(),
      nonce,
      expirationTimestamp.toString(),
      receiverAddress,
      '0x',
      originator,
    ].join(':');

    const textEncoder = new TextEncoder();
    const encoded = textEncoder.encode(payload);
    const messageHash = await crypto.subtle.digest('SHA-256', encoded);
    const signature = await this.sign(new Uint8Array(messageHash));
    return this.codec.decode(signature);
  }

  /** @returns base58 encoded signature */
  async signSolTransferRequest({
    bankProgramAddress,
    authority,
    senderAddress,
    receiverAddress,
    amount,
    feeAmount,
    nonce,
    expirationTimestamp,
    originator,
    data = '0x',
  }: TransferParams): Promise<string> {
    const payload = [
      'TRANSFER_SOL',
      bankProgramAddress,
      authority,
      senderAddress,
      receiverAddress,
      amount.toString(),
      feeAmount.toString(),
      nonce.toString(),
      expirationTimestamp.toString(),
      data,
      originator,
    ].join(':');

    const textEncoder = new TextEncoder();
    const encoded = textEncoder.encode(payload);
    const messageHash = await crypto.subtle.digest('SHA-256', encoded);
    const signature = await this.sign(new Uint8Array(messageHash));
    return this.codec.decode(signature);
  }

  /** @returns base58 encoded signature */
  async signSolWithdrawalRequest({
    bankProgramAddress,
    authority,
    senderAddress,
    receiverAddress,
    amount,
    feeAmount,
    nonce,
    expirationTimestamp,
    originator,
  }: {
    bankProgramAddress: string;
    authority: string;
    senderAddress: string;
    receiverAddress: string;
    amount: bigint;
    feeAmount: bigint;
    nonce: string;
    expirationTimestamp: number;
    originator: string;
  }): Promise<string> {
    const payload = [
      'WITHDRAW_SOL',
      bankProgramAddress,
      authority,
      senderAddress,
      receiverAddress,
      amount.toString(),
      feeAmount.toString(),
      nonce.toString(),
      expirationTimestamp.toString(),
      '0x',
      originator,
    ].join(':');

    const textEncoder = new TextEncoder();
    const encoded = textEncoder.encode(payload);
    const messageHash = await crypto.subtle.digest('SHA-256', encoded);
    const signature = await this.sign(new Uint8Array(messageHash));
    return this.codec.decode(signature);
  }

  async signConditionalSolTransferRequest(transfer: ConditionalTransferParams) {
    const fact = computeEthereumBridgeFact({
      conditionalAmount: transfer.conditionalAmount,
      conditionalSalt: transfer.conditionalSalt,
      conditionalReceiverAddress: transfer.conditionalReceiverAddress,
      conditionalBridgeCallerAddress: transfer.originator,
      conditionalNetwork: transfer.conditionalNetwork,
      conditionalCurrency: transfer.conditionalCurrency,
    });

    return this.signSolTransferRequest({
      ...transfer,
      data: fact,
    });
  }
}

export const computeBridgeFact = (condition: {
  conditionalAmount: bigint;
  conditionalSalt: string;
  conditionalReceiverAddress: string;
  conditionalBridgeCallerAddress: string;
  conditionalNetwork: string;
  conditionalCurrency: string;
}) => {
  const salt = hexToBytes(condition.conditionalSalt as `0x${string}`);
  const amount = getU64Codec({ endian: Endian.Big }).encode(
    condition.conditionalAmount
  );
  const receiver = getBase58Codec().encode(
    condition.conditionalReceiverAddress
  );
  const bridge = getBase58Codec().encode(
    condition.conditionalBridgeCallerAddress
  );

  const factBytes = new Uint8Array([...salt, ...amount, ...receiver]);

  const fact = keccak256(factBytes);

  const wrappedFact = keccak256(
    new Uint8Array([...hexToBytes(fact), ...bridge])
  );

  return wrappedFact;
};
